import { useEffect, useEffectEvent, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useMatchRoute } from '@mint/routing';

import type { DataFilePreview } from '../../api/types.js';
import { useDataFileRouteApi } from '../../hooks/useImportHubRouteApi.js';
import { useDataFilePreview } from '../../queries/index.js';
import {
  createClientStepsConfig,
  getDataFileClientStep,
} from '../../utils/index.js';

export const useSyncDataFileRoute = (
  dataFileId: string,
  dataFilePreview: DataFilePreview,
) => {
  const matchRoute = useMatchRoute();
  const navigate = useDataFileRouteApi().useNavigate();
  const clientStepsConfig = useDataFileClientStepsConfig(dataFileId);

  const targetStep = getDataFileClientStep(dataFilePreview);

  const redirectToTargetStep = useEffectEvent(() => {
    const { link } = clientStepsConfig[targetStep];
    const isOnTargetRoute = matchRoute({ to: link.to, fuzzy: true });

    if (!isOnTargetRoute) {
      void navigate({ ...link, replace: true });
    }
  });

  useEffect(() => {
    /* leaving the wizard is handled by the action that finishes the import */
    if (targetStep === 'success') {
      return;
    }

    redirectToTargetStep();
  }, [targetStep]);
};

const useDataFileClientStepsConfig = (dataFileId: string) => {
  const { t } = useTranslation('@myn/import-hub');
  const {
    data: { granularityLevel },
  } = useDataFilePreview(dataFileId);

  return useMemo(
    () =>
      createClientStepsConfig({
        t,
        granularityLevel,
      }),
    [t, granularityLevel],
  );
};
